import { useMutation, useQueryClient } from "@tanstack/vue-query";
import Unipile from "@/services/unipile/unipile";
import type { DeleteEmailParams } from "./types";

/**
 * Composable for deleting emails or threads with optimistic cache removal.
 */
export function useDeleteEmail() {
  const queryClient = useQueryClient();

  const deleteEmailMutation = useMutation({
    mutationFn: async (params: DeleteEmailParams) => {
      if (params.isThread && params.threadId) {
        await Unipile.deleteThread(params.threadId, params.accountId);
      } else {
        const emailId = params.emailId;
        await Unipile.deleteEmail(emailId, params.accountId);
      }
    },
    onSuccess: (_data, variables) => {
      // Remove the deleted item from conversations cache optimistically
      queryClient.setQueriesData<any[]>({ queryKey: ["email-conversations"] }, (old) => {
        if (!old) return old;
        return old.filter((conv) => conv.id !== variables.conversationId);
      });

      // Invalidate thread cache if it was a thread
      if (variables.isThread && variables.threadId) {
        queryClient.removeQueries({
          queryKey: ["email-thread", variables.threadId, variables.accountId],
        });
      }

      // Invalidate conversations to refetch
      queryClient.invalidateQueries({
        queryKey: ["email-conversations"],
      });
    },
  });

  return {
    deleteEmail: deleteEmailMutation.mutateAsync,
    isDeleting: deleteEmailMutation.isPending,
    deleteError: deleteEmailMutation.error,
  };
}
